import { TranslatedMessages } from '@color/i18n';
import { ColorIso639Code, Locale, retry } from '@color/lib';

import EN_MESSAGES from './languages/en.json';

const DEFAULT_MESSAGES_LOCALE = Locale.ENGLISH;
export const DEFAULT_MESSAGES = EN_MESSAGES;

/*
 * Webpack settings resolves dynamic module import paths at build time. If the import path is
 * interpolated or partially dynamic, then Webpack will snarf and bundle all files that it can
 * infer based on the directory of the interpolated string.
 *  i.e `./languages/${foo}`, will include all files and folders under ./languages
 *
 * English the default LOCALE, so it is excluded from dynamic importing.
 */
function getMessageImportByIso639Code(
  iso639Code: string
): Promise<{ default: Record<string, string> }> {
  switch (iso639Code) {
    case ColorIso639Code.SPANISH:
      return import('./languages/es.json');
    case ColorIso639Code.CHINESE_SIMPLIFIED:
      return import('./languages/zh_Hans.json');
    case ColorIso639Code.CHINESE_TRADITIONAL:
      return import('./languages/zh_Hant.json');
    case ColorIso639Code.PORTUGUESE:
      return import('./languages/pt.json');
    case ColorIso639Code.HAITIAN_CREOLE:
      return import('./languages/ht.json');
    case ColorIso639Code.KOREAN:
      return import('./languages/ko.json');
    case ColorIso639Code.RUSSIAN:
      return import('./languages/ru.json');
    case ColorIso639Code.VIETNAMESE:
      return import('./languages/vi.json');
    case ColorIso639Code.ARABIC:
      return import('./languages/ar.json');
    case ColorIso639Code.FILIPINO:
      return import('./languages/fil.json');
    case ColorIso639Code.SAMOAN:
      return import('./languages/sm.json');
    case ColorIso639Code.KHMER:
      return import('./languages/km.json');
    case ColorIso639Code.POLISH:
      return import('./languages/pl.json');
    case ColorIso639Code.UKRAINIAN:
      return import('./languages/uk.json');
    case ColorIso639Code.URDU:
      return import('./languages/ur.json');
    case ColorIso639Code.FRENCH:
      return import('./languages/fr.json');
    case ColorIso639Code.SOMALI:
      return import('./languages/so.json');
    case ColorIso639Code.FARSI:
      return import('./languages/fa.json');
    case ColorIso639Code.CAPE_VERDEAN_CREOLE:
      return import('./languages/kea.json');
    case ColorIso639Code.AMHARIC:
      return import('./languages/am.json');
    case ColorIso639Code.MARSHALLESE:
      return import('./languages/mh.json');
    case ColorIso639Code.HMONG:
      return import('./languages/hmn.json');
    case ColorIso639Code.HINDI:
      return import('./languages/hi.json');
    case ColorIso639Code.HUNGARIAN:
      return import('./languages/hu.json');
    case ColorIso639Code.DUTCH:
      return import('./languages/nl.json');
    default:
      return Promise.resolve({ default: EN_MESSAGES });
  }
}

/*
 * Home's translation files live in Home. There presently is not a way to dynamically
 * import files or modules in Home from a dependency like @color/i18n. Thusly,
 * Home must provide the dynamic import in the form of a callback to TranslatedMessages.
 *
 * The import.meta attribute does contain a baseUrl equivalent, but that's not a feature
 * currently support by CRA's Webpack settings.
 */
const loadTranslatedMessages = async (
  iso639Code: ColorIso639Code
): Promise<Record<string, string>> => {
  const messages: Record<string, string> = (
    await retry(() => getMessageImportByIso639Code(iso639Code))
  ).default;
  return {
    ...DEFAULT_MESSAGES,
    ...messages,
  };
};

const messageLoader = new TranslatedMessages({
  defaultLocale: DEFAULT_MESSAGES_LOCALE,
  defaultMessages: DEFAULT_MESSAGES,
  loadTranslatedMessages,
});

/**
 * This function lazily loads language content for the locale.
 * IMPORTANT: These JSON translations are large, and impact initial page load.
 * The size of our payload is directly dependent on the content that we bundle.
 * The reason why these are split is to only load the payload relevant to the area of the app.
 * `retry` allows the compiler to code-split into lazy loaded bundles.
 *
 * On mobile, this could be several seconds to load.
 * In addition, these files are several hundred kilobytes, which could overwhelm
 * some mobile browsers.
 * @param locale
 * @returns
 */
export async function getMessages(locale: Locale): Promise<Record<string, string>> {
  return messageLoader.getMessages(locale);
}
